import { Injectable } from '@angular/core';

export interface FileValidationResult {
  valid: boolean;
  messageKey?: string;
  message?: string;
}

/** Client-side upload checks (UX only — backend remains authoritative). */
@Injectable({ providedIn: 'root' })
export class FileUploadValidationService {
  static readonly DEFAULT_MAX_BYTES = 25 * 1024 * 1024;
  static readonly KNOWLEDGE_LIBRARY_MAX_BYTES = 250 * 1024 * 1024;
  static readonly MILESTONE_MAX_FILES = 4;
  static readonly INDICATOR_PROOF_MAX_FILES = 20;

  private readonly allowedExtensions = new Set([
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt',
    '.png', '.jpg', '.jpeg', '.gif', '.webp'
  ]);

  private readonly blockedExtensions = new Set([
    '.exe', '.bat', '.cmd', '.com', '.msi', '.scr', '.ps1', '.vbs', '.js', '.jar', '.sh', '.dll'
  ]);

  validateFile(
    file: File,
    options?: { maxBytes?: number; allowedExtensions?: Set<string> }
  ): FileValidationResult {
    const maxBytes = options?.maxBytes ?? FileUploadValidationService.DEFAULT_MAX_BYTES;
    const allowed = options?.allowedExtensions ?? this.allowedExtensions;

    if (!file || file.size <= 0) {
      return { valid: false, messageKey: 'invalid_file' };
    }

    if (file.size > maxBytes) {
      return { valid: false, messageKey: 'file_too_large' };
    }

    const extension = this.getExtension(file.name);
    if (!extension || this.blockedExtensions.has(extension) || !allowed.has(extension)) {
      return { valid: false, messageKey: 'unsupported_file_type' };
    }

    return { valid: true };
  }

  validateFiles(
    files: FileList | File[],
    options?: { maxBytes?: number; maxCount?: number; allowedExtensions?: Set<string> }
  ): FileValidationResult {
    const list = Array.from(files);
    if (!list.length) {
      return { valid: false, messageKey: 'no_files_selected' };
    }

    if (options?.maxCount != null && list.length > options.maxCount) {
      return { valid: false, messageKey: 'too_many_files' };
    }

    for (const file of list) {
      const result = this.validateFile(file, options);
      if (!result.valid) {
        return result;
      }
    }

    return { valid: true };
  }

  getExtension(fileName: string): string {
    const base = fileName.split(/[/\\]/).pop() ?? fileName;
    const idx = base.lastIndexOf('.');
    if (idx <= 0) {
      return '';
    }
    return base.slice(idx).toLowerCase();
  }
}
